import React from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ArrowDownIcon from '@atlaskit/icon/core/arrow-down';
import ArrowUpIcon from '@atlaskit/icon/core/arrow-up';
import ClipboardIcon from '@atlaskit/icon/core/clipboard';
import CrossIcon from '@atlaskit/icon/core/cross';
import MoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import Tooltip from '@atlaskit/tooltip';

import { useContextMenuActions } from '../../hooks/useContextMenuActions';
import type { FlagsContainer } from '../audio-player/AudioContainer';

import type { PlaylistItemData } from './PlaylistItem';

const messages = defineMessages({
	moreOptions: {
		id: 'ai-audio.playlist.more-options',
		defaultMessage: 'More options',
		description: 'Tooltip and label for the more options button on a playlist item',
	},
	moveUp: {
		id: 'ai-audio.playlist.move-up',
		defaultMessage: 'Move up',
		description: 'Menu item to move a playlist item up in the list',
	},
	moveDown: {
		id: 'ai-audio.playlist.move-down',
		defaultMessage: 'Move down',
		description: 'Menu item to move a playlist item down in the list',
	},
	copyLink: {
		id: 'ai-audio.playlist.copy-link',
		defaultMessage: 'Copy link to audio',
		description: 'Menu item to copy the link to the audio',
	},
	removeFromPlaylist: {
		id: 'ai-audio.playlist.remove-from-playlist',
		defaultMessage: 'Remove from playlist',
		description: 'Menu item to remove an item from the playlist',
	},
});

type PlaylistItemContextualMenuProps = {
	onMenuOpenChange?: (isOpen: boolean) => void;
	isFirstItem?: boolean;
	isLastItem?: boolean;
	data: PlaylistItemData;
	flags: FlagsContainer;
	currentIndex: number;
	allItems: PlaylistItemData[];
	onOptimisticReorder: (fromIndex: number, toIndex: number) => PlaylistItemData[];
	onReorderSuccess: () => void;
	onReorderError: () => void;
};

export const PlaylistItemContextualMenu = ({
	onMenuOpenChange,
	isFirstItem,
	isLastItem,
	data,
	flags,
	currentIndex,
	allItems,
	onOptimisticReorder,
	onReorderSuccess,
	onReorderError,
}: PlaylistItemContextualMenuProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { removeTrack, reorderTrack } = useContextMenuActions(flags);
	const { mainSource, supportingSources, trackType } = data;

	const handleOpenChange = (args: { isOpen: boolean }) => {
		onMenuOpenChange?.(args.isOpen);
	};

	const handleMoveUp = async () => {
		if (isFirstItem || currentIndex <= 0) {
			return;
		}

		const newIndex = currentIndex - 1;
		onOptimisticReorder(currentIndex, newIndex);

		const targetTrack = {
			mainSource,
			supportingSources,
			trackType,
		};

		const newReferenceIndex = currentIndex - 2;
		const referenceItem = newReferenceIndex >= 0 ? allItems[newReferenceIndex] : null;
		const referenceTrack = referenceItem
			? {
					mainSource: referenceItem.mainSource,
					supportingSources: referenceItem.supportingSources,
					trackType: referenceItem.trackType,
				}
			: undefined; // undefined means move to beginning

		try {
			await reorderTrack(targetTrack, referenceTrack, data.title);
			onReorderSuccess();
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
		} catch (error) {
			onReorderError();
		}
	};

	const handleMoveDown = async () => {
		if (isLastItem || currentIndex >= allItems.length - 1) {
			return;
		}

		const newIndex = currentIndex + 1;
		onOptimisticReorder(currentIndex, newIndex);

		const targetTrack = {
			mainSource,
			supportingSources,
			trackType,
		};

		// When moving down, we want to place our item after the item that's currently below us
		const referenceItem = allItems[currentIndex + 1];
		const referenceTrack = {
			mainSource: referenceItem.mainSource,
			supportingSources: referenceItem.supportingSources,
			trackType: referenceItem.trackType,
		};

		try {
			await reorderTrack(targetTrack, referenceTrack, data.title);
			onReorderSuccess();
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
		} catch (error) {
			onReorderError();
		}
	};

	const handleCopyLink = () => {
		// TODO: Implement copy link action
	};

	const handleRemove = async () => {
		await removeTrack(mainSource, supportingSources, trackType, data.title);
	};

	return (
		<Tooltip content={formatMessage(messages.moreOptions)}>
			<DropdownMenu
				onOpenChange={handleOpenChange}
				trigger={({ triggerRef, ...triggerProps }) => (
					<IconButton
						{...triggerProps}
						ref={triggerRef}
						icon={MoreIcon}
						label={formatMessage(messages.moreOptions)}
						appearance="subtle"
						shape="circle"
					/>
				)}
			>
				<DropdownItemGroup>
					<DropdownItem
						onClick={handleMoveUp}
						elemBefore={<ArrowUpIcon label="" />}
						isDisabled={isFirstItem}
					>
						{formatMessage(messages.moveUp)}
					</DropdownItem>
					<DropdownItem
						onClick={handleMoveDown}
						elemBefore={<ArrowDownIcon label="" />}
						isDisabled={isLastItem}
					>
						{formatMessage(messages.moveDown)}
					</DropdownItem>
					<DropdownItem onClick={handleCopyLink} elemBefore={<ClipboardIcon label="" />}>
						{formatMessage(messages.copyLink)}
					</DropdownItem>
					{/* TODO: Update the remove item icon once its available */}
					<DropdownItem onClick={handleRemove} elemBefore={<CrossIcon label="" />}>
						{formatMessage(messages.removeFromPlaylist)}
					</DropdownItem>
				</DropdownItemGroup>
			</DropdownMenu>
		</Tooltip>
	);
};
